import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import React, { Component } from 'react';
import Box from '../Box';
import { getBoxSize } from '../system';
import { deselectCurrentTextRange, getTextWidth, selectFullTextRange } from '../utils';
import InputContext from './InputContext';
import { inputs, inputLabels } from './utils';
var resetStyles = {
  appearance: 'none',
  outline: 0,
  boxShadow: 'none',
  MozAppearance: 'textfield',
  '::-ms-clear': {
    display: 'none'
  },
  '::-webkit-clear-button': {
    display: 'none'
  },
  '::-webkit-inner-spin-button, ::-webkit-outer-spin-button': {
    display: 'none'
  },
  '::-webkit-search-cancel-button': {
    display: 'none'
  }
};

var InputField = /*#__PURE__*/function (_Component) {
  _inheritsLoose(InputField, _Component);

  function InputField(props) {
    var _this;

    _this = _Component.call(this, props) || this;

    _defineProperty(_assertThisInitialized(_this), "setNode", function (node) {
      _this.node = node;

      if (_this.props.innerRef) {
        _this.props.innerRef(node);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "getAutoWidthValue", function (defaultValue) {
      return _this.props.autoWidth === true ? _this.props.value !== null && _this.props.value !== undefined ? _this.props.value : defaultValue : _this.props.autoWidth;
    });

    _defineProperty(_assertThisInitialized(_this), "preventFocus", function (event) {
      if (_this.props.highlightOnInteraction && event.target === document.activeElement) {
        event.preventDefault();
        selectFullTextRange(event.target);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleChange", function (event) {
      if (_this.props.autoWidth !== undefined) {
        if (_this.props.autoWidth === true) {
          _this.setWidth(event.target.value);
        } else {
          _this.shouldUpdateWidth = true;
        }
      }

      if (_this.props.onChange) {
        _this.props.onChange(event);
      }

      _this.canHighlight = true;
    });

    _defineProperty(_assertThisInitialized(_this), "handleKeyDown", function (event) {
      _this.canHighlight = true;

      if (_this.props.onKeyDown) {
        _this.props.onKeyDown(event);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleFocus", function (event) {
      if (_this.props.highlightOnInteraction) {
        selectFullTextRange(event.target);
      }

      if (_this.inputBox) {
        _this.inputBox.setState({
          isFocused: true
        });
      }

      if (_this.props.onFocus) {
        _this.props.onFocus(event);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleBlur", function (event) {
      _this.canHighlight = false;

      if (_this.props.highlightOnInteraction) {
        deselectCurrentTextRange();
      }

      if (_this.inputBox) {
        _this.inputBox.setState({
          isFocused: false
        });
      }

      if (_this.props.autoWidth !== undefined && _this.props.placeholder !== undefined && event.target.value === '') {
        _this.setWidth(_this.props.placeholder);
      }

      if (_this.props.onBlur) {
        _this.props.onBlur(event);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "focus", function () {
      _this.node.focus();
    });

    _defineProperty(_assertThisInitialized(_this), "blur", function () {
      _this.node.blur();
    });

    _this.state = {
      isHovered: false
    };
    _this.shouldUpdateWidth = false;

    if (props.id) {
      inputs[props.id] = _assertThisInitialized(_this);
    }

    return _this;
  }

  var _proto = InputField.prototype;

  _proto.componentDidMount = function componentDidMount() {
    if (this.props.autoWidth !== undefined) {
      var autoWidthValue = this.getAutoWidthValue(this.props.defaultValue !== undefined ? this.props.defaultValue : this.props.placeholder);

      if (autoWidthValue !== undefined) {
        this.setWidth(autoWidthValue);
      }
    }
  };

  _proto.componentDidUpdate = function componentDidUpdate(lastProps) {
    if (this.shouldUpdateWidth || this.props.autoWidth !== undefined && this.props.value !== undefined && this.props.value !== lastProps.value) {
      this.setWidth(this.getAutoWidthValue(this.props.placeholder));
      this.shouldUpdateWidth = false;
    }

    if (this.canHighlight && this.props.highlightOnInteraction) {
      selectFullTextRange(this.node);
      this.canHighlight = false;
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.props.id) {
      delete inputs[this.props.id];
    }
  };

  _proto.setWidth = function setWidth(text) {
    var autoWidthType = this.props.autoWidthType;
    var textWidth = getTextWidth(text, this.node);

    if (textWidth !== this._lastTextWidth) {
      var cursorWidth = 1;

      var _getComputedStyle = getComputedStyle(this.node),
          borderLeftWidth = _getComputedStyle.borderLeftWidth,
          borderRightWidth = _getComputedStyle.borderRightWidth,
          paddingLeft = _getComputedStyle.paddingLeft,
          paddingRight = _getComputedStyle.paddingRight;

      var totalWidth = [borderLeftWidth, borderRightWidth, cursorWidth, paddingLeft, paddingRight, textWidth].reduce(function (total, val) {
        return total + parseInt(val, 10);
      }, 0);

      if (autoWidthType === 'width' || autoWidthType === 'both') {
        this.node.style.width = totalWidth + "px";
      }

      if (autoWidthType === 'basis' || autoWidthType === 'both') {
        this.node.style.flexBasis = totalWidth + "px";
      }

      this._lastTextWidth = textWidth;
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props = this.props,
        autoWidth = _this$props.autoWidth,
        autoWidthType = _this$props.autoWidthType,
        css = _this$props.css,
        disabled = _this$props.disabled,
        innerRef = _this$props.innerRef,
        onBlur = _this$props.onBlur,
        onChange = _this$props.onChange,
        onFocus = _this$props.onFocus,
        onKeyDown = _this$props.onKeyDown,
        onMouseDown = _this$props.onMouseDown,
        onMouseUp = _this$props.onMouseUp,
        placeholderColor = _this$props.placeholderColor,
        highlightOnInteraction = _this$props.highlightOnInteraction,
        size = _this$props.size,
        restProps = _objectWithoutPropertiesLoose(_this$props, ["autoWidth", "autoWidthType", "css", "disabled", "innerRef", "onBlur", "onChange", "onFocus", "onKeyDown", "onMouseDown", "onMouseUp", "placeholderColor", "highlightOnInteraction", "size"]);

    var placeholderColorStyles = {
      '::placeholder': {
        color: placeholderColor
      },
      '::-webkit-input-placeholder': {
        color: placeholderColor
      },
      ':-moz-placeholder': {
        color: placeholderColor
      },
      ':-ms-input-placeholder': {
        color: placeholderColor
      }
    };
    return /*#__PURE__*/React.createElement(InputContext.Consumer, null, function (inputBox) {
      var _ref = inputBox ? inputBox.getBoxStyles() : getBoxSize(size),
          fontSize = _ref.fontSize,
          lineHeight = _ref.lineHeight;

      if (!_this2.inputBox) {
        _this2.inputBox = inputBox;
      }

      if (inputLabels[_this2.props.id]) {
        restProps['aria-labelledby'] = _this2.props.id + "-label";
      }

      if (_this2.state.isHovered && restProps.hover) {
        Object.assign(restProps, restProps.hover);
      }

      return /*#__PURE__*/React.createElement(Box, _extends({
        as: "input",
        innerRef: _this2.setNode,
        disabled: disabled,
        grow: 1,
        padding: 0,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: fontSize,
        lineHeight: lineHeight,
        minHeight: lineHeight,
        onFocus: _this2.handleFocus,
        onBlur: _this2.handleBlur,
        onChange: _this2.handleChange,
        onKeyDown: _this2.handleKeyDown,
        onMouseDown: _this2.preventFocus,
        onMouseUp: _this2.preventFocus,
        css: [resetStyles, placeholderColorStyles, css]
      }, restProps));
    });
  };

  return InputField;
}(Component);

_defineProperty(InputField, "defaultProps", {
  placeholderColor: 'foregroundTertiary',
  autoWidthType: 'both'
});

InputField.displayName = 'Input.InputField';
export default InputField;